import { useState, type ReactElement } from "react";
import {
  Button,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { Columns3Icon } from "@aeriosiq/ui/icons";
import { controlBandButtonSx } from "../theme/compact.js";
/**
 * Shared column-visibility toggle (lifted from
 * `apps/lyse/src/features/inspections-list/ColumnVisibilityButton.tsx`
 * into `@aeriosiq/ui/data-grid` per Story 054 §Open Q11 / B-054.2).
 *
 * Toolbar button with a checkbox menu — one row per column. Persistence
 * (e.g. to localStorage) is the consumer's concern; this primitive is
 * a controlled component over `(model, onChange)`.
 *
 * Columns flagged `pinned: true` are excluded from the menu so consumers
 * (Sites' "Edit" column, future bulk-select rails) can keep critical
 * cells always-visible.
 */
export interface ColumnDescriptor {
  field: string;
  headerName: string;
  /** Reserved column that must always stay visible (e.g. the row-action
   *  button). Hidden from the menu so users can't accidentally hide it. */
  pinned?: boolean;
}

export interface ColumnVisibilityButtonProps {
  columns: ColumnDescriptor[];
  /** model[field] === false means hidden. Missing keys default to visible. */
  model: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}

export function ColumnVisibilityButton({
  columns,
  model,
  onChange,
}: ColumnVisibilityButtonProps): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl !== null;
  const visibleCount = columns.filter(
    (c) => (c.pinned ?? false) || model[c.field] !== false,
  ).length;
  const handleToggle = (field: string): void => {
    onChange({ ...model, [field]: model[field] === false });
  };
  const handleShowAll = (): void => {
    const next: Record<string, boolean> = {};
    for (const c of columns) next[c.field] = true;
    onChange(next);
  };

  return (
    <>
      <Button
        sx={controlBandButtonSx}
        size="small"
        variant="text"
        color="inherit"
        startIcon={<Columns3Icon fontSize="small" />}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? "columns-menu" : undefined}
      >
        Columns ({String(visibleCount)})
      </Button>
      <Menu
        id="columns-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
        MenuListProps={{ dense: true, "aria-label": "Columns" }}
      >
        {columns
          .filter((c) => !c.pinned)
          .map((c) => {
            const isVisible = model[c.field] !== false;
            return (
              <MenuItem
                key={c.field}
                onClick={() => {
                  handleToggle(c.field);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    checked={isVisible}
                    edge="start"
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary={c.headerName} />
              </MenuItem>
            );
          })}
        <Divider />
        <MenuItem onClick={handleShowAll}>
          <ListItemText primary="Show all" />
        </MenuItem>
      </Menu>
    </>
  );
}
